import { useTranslation } from 'react-i18next'

const formatMoney = value => {
	const number = Number(`${value}`.replace(',', '.'))
	if (!Number.isFinite(number)) return '0'
	return number.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

export default function ManualEntriesTable({
	entries,
	entriesTotal,
	onAdd,
	onChange,
	onRemove,
	disabled,
	translationKey = 'rozliczenia.details.entries',
	totalPrefix = '',
	showCompany = false,
	companies = [],
}) {
	const { t } = useTranslation()
	const rows = Array.isArray(entries) ? entries : []
	const companyOptions = Array.isArray(companies) ? companies : []
	const colSpan = showCompany ? 5 : 4
	const totalLabelColSpan = showCompany ? 3 : 2

	const handleCompanyChange = (entryId, companyId) => {
		const company = companyOptions.find(item => `${item.id}` === `${companyId}`)
		onChange(entryId, {
			companyId: companyId || '',
			companyName: company?.name || '',
		})
	}

	return (
		<div className="mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h2 className="text-lg font-semibold text-gray-900">
						{t(`${translationKey}.title`)}
					</h2>
					<p className="mt-1 text-sm text-gray-500">
						{t(`${translationKey}.subtitle`)}
					</p>
				</div>
				<button
					type="button"
					onClick={onAdd}
					disabled={disabled}
					className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{t(`${translationKey}.add`)}
				</button>
			</div>

			<div className="mt-4 overflow-x-auto">
				<table className="min-w-full border-collapse text-sm">
					<thead>
						<tr className="bg-gray-50">
							<th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
								{t(`${translationKey}.date`)}
							</th>
							{showCompany && (
								<th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700 min-w-48">
									{t(`${translationKey}.company`)}
								</th>
							)}
							<th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
								{t(`${translationKey}.note`)}
							</th>
							<th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700 w-40">
								{t(`${translationKey}.value`)}
							</th>
							<th className="border border-gray-200 px-3 py-2 text-right font-semibold text-gray-700 w-28">
								{t(`${translationKey}.actions`)}
							</th>
						</tr>
					</thead>
					<tbody>
						{rows.length === 0 ? (
							<tr>
								<td colSpan={colSpan} className="border border-gray-200 px-3 py-6 text-center text-gray-500">
									{t(`${translationKey}.empty`)}
								</td>
							</tr>
						) : rows.map(entry => (
							<tr key={entry._id}>
								<td className="border border-gray-200 px-2 py-2">
									<input
										type="date"
										value={entry.date || ''}
										onChange={event => onChange(entry._id, 'date', event.target.value)}
										disabled={disabled}
										className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm disabled:opacity-50"
									/>
								</td>
								{showCompany && (
									<td className="border border-gray-200 px-2 py-2">
										<select
											value={entry.companyId || ''}
											onChange={event => handleCompanyChange(entry._id, event.target.value)}
											disabled={disabled}
											className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm disabled:opacity-50"
										>
											<option value="">{t(`${translationKey}.companyPlaceholder`)}</option>
											{companyOptions.map(company => (
												<option key={company.id} value={company.id}>
													{company.name}
												</option>
											))}
										</select>
									</td>
								)}
								<td className="border border-gray-200 px-2 py-2">
									<input
										type="text"
										value={entry.note || ''}
										onChange={event => onChange(entry._id, 'note', event.target.value)}
										disabled={disabled}
										placeholder={t(`${translationKey}.notePlaceholder`)}
										className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm disabled:opacity-50"
									/>
								</td>
								<td className="border border-gray-200 px-2 py-2">
									<input
										type="number"
										step="0.01"
										value={entry.value}
										onChange={event => onChange(entry._id, 'value', event.target.value)}
										disabled={disabled}
										className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm disabled:opacity-50"
									/>
								</td>
								<td className="border border-gray-200 px-2 py-2 text-right">
									<button
										type="button"
										onClick={() => onRemove(entry._id)}
										disabled={disabled}
										className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
									>
										{t(`${translationKey}.remove`)}
									</button>
								</td>
							</tr>
						))}
					</tbody>
					<tfoot>
						<tr className="bg-gray-50 font-semibold">
							<td colSpan={totalLabelColSpan} className="border border-gray-200 px-3 py-2 text-right">
								{t(`${translationKey}.total`)}
							</td>
							<td className="border border-gray-200 px-3 py-2">
								{totalPrefix}{formatMoney(entriesTotal)} PLN
							</td>
							<td className="border border-gray-200" />
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	)
}
